import { fetcher } from '#lib/backendFetcher';

/**
 * Searches for companies by name via autocomplete.
 * @param {string} query - Search term (minimum 2 characters)
 * @returns {Promise<Array<Object>>}
 */
export const searchCompaniesByName = async (query) => {
	if (!query || query.trim().length < 2) return [];

	const result = await fetcher(`/api/v1/search/autocomplete/companies?q=${encodeURIComponent(query.trim())}`);

	return result.data ?? [];
};

/**
 * Selects a company from the autocomplete results and optionally creates a prospect query.
 * @param {string} companyId - UUID of the company
 * @returns {Promise<string>} companyId of the selected company (used for navigation)
 */
export const selectCompanyFromAutocomplete = async (companyId) => {
	const result = await fetcher('/api/v1/search/autocomplete/companies/select', {
		method: 'POST',
		body: { companyId },
	});

	return result.data.companyId;
};

/**
 * Fetches the full company details by the pool company id.
 * @param {string} companyId - UUID of the company
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const fetchCompanyDetail = async (companyId) => {
	try {
		const data = await fetcher(`/api/v1/leads/companies/${companyId}`);
		return {
			data: {
				...data,
				companyId: data.id,
				rating: data.rating ?? null,
				reviewCount: data.reviewCount ?? null,
			},
			error: null,
		};
	} catch (error) {
		return { data: null, error };
	}
};

/**
 * Fetches the financial history of a company.
 * @param {string} companyId - UUID of the company
 * @returns {Promise<{data: Array<Object>, error: Error|null}>}
 */
export const fetchCompanyFinancialHistory = async (companyId) => {
	if (!companyId) return { data: [], error: null };

	try {
		const result = await fetcher(`/api/v1/leads/companies/${companyId}/financials`);
		return { data: result.financials ?? [], error: null };
	} catch (error) {
		return { data: [], error };
	}
};

/**
 * Fetches the events of a company.
 * @param {string} companyId - UUID of the company
 * @returns {Promise<{data: Array<Object>, error: Error|null}>}
 */
export const fetchCompanyEvents = async (companyId) => {
	if (!companyId) return { data: [], error: null };

	try {
		const result = await fetcher(`/api/v1/leads/companies/${companyId}/events`);
		return { data: result.events ?? [], error: null };
	} catch (error) {
		return { data: [], error };
	}
};

/**
 * Fetches the contact persons of a company.
 * @param {string} companyId - UUID of the company
 * @returns {Promise<{data: Array<Object>, error: Error|null}>}
 */
export const fetchCompanyContacts = async (companyId) => {
	if (!companyId) return { data: [], error: null };

	try {
		const result = await fetcher(`/api/v1/leads/companies/${companyId}/contacts`);
		return { data: result.contacts ?? [], error: null };
	} catch (error) {
		return { data: [], error };
	}
};

/**
 * Fetches the managing directors of a company.
 * @param {string} companyId - UUID of the company
 * @returns {Promise<{data: Array<Object>, error: Error|null}>}
 */
export const fetchCompanyDirectors = async (companyId) => {
	if (!companyId) return { data: [], error: null };

	try {
		const result = await fetcher(`/api/v1/leads/companies/${companyId}/directors`);
		return { data: result.directors ?? [], error: null };
	} catch (error) {
		return { data: [], error };
	}
};
